import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Phone, AtSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoStacked } from "@/components/Logo";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useBrandName } from "@/context/BrandingContext";

export function LoginPage() {
  const { login } = useAuth();
  const brandName = useBrandName();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      toast.success("Selamat datang kembali!");
      if (u.role === "admin" || u.role === "owner") navigate("/admin");
      else navigate(next);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Masuk ke Akun" subtitle={`Selamat datang kembali di ${brandName}`}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Username atau Email</Label>
          <div className="relative mt-1">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              type="text"
              autoComplete="username"
              required
              placeholder="Masukkan username atau email"
              className="pl-9 h-11 rounded-xl"
              data-testid="login-identifier"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Password</Label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              required
              className="pl-9 h-11 rounded-xl"
              data-testid="login-password"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs brand-text-primary font-semibold hover:underline" data-testid="forgot-password-link">Lupa password?</Link>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 brand-btn-primary rounded-xl font-bold" data-testid="login-submit">
          {loading ? "Memproses..." : "Masuk"}
        </Button>
        <div className="text-center text-sm text-[#5C5C5C]">
          Belum punya akun? <Link to="/register" className="brand-text-primary font-bold" data-testid="goto-register">Daftar gratis</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const [form, setForm] = useState({ username: "", name: "", email: "", phone: "", password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation
    const username = form.username.trim().toLowerCase();
    if (!/^[a-zA-Z0-9._]{4,30}$/.test(username)) {
      toast.error("Username hanya boleh huruf, angka, titik, underscore (min 4 karakter, tanpa spasi)");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error("Password dan konfirmasi password tidak sama");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, username, email: form.email.trim().toLowerCase() });
      toast.success("Akun berhasil dibuat!");
      navigate("/");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Daftar Akun Baru" subtitle="Buat akun dan nikmati promo eksklusif">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Username</Label>
          <div className="relative mt-1">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input
              value={form.username}
              onChange={(e) => upd("username", e.target.value.replace(/\s/g, ""))}
              required
              minLength={4}
              maxLength={30}
              autoComplete="username"
              placeholder="contoh: johndoe_88"
              className="pl-9 h-11 rounded-xl"
              data-testid="register-username"
            />
          </div>
          <div className="text-[11px] text-[#5C5C5C] mt-1">Huruf, angka, titik, underscore (4–30 karakter, tanpa spasi)</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Nama Lengkap</Label>
          <div className="relative mt-1">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required className="pl-9 h-11 rounded-xl" data-testid="register-name" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Email</Label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={form.email} onChange={(e) => upd("email", e.target.value)} type="email" autoComplete="email" required className="pl-9 h-11 rounded-xl" data-testid="register-email" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">No. WhatsApp</Label>
          <div className="relative mt-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="08xxxx" className="pl-9 h-11 rounded-xl" data-testid="register-phone" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Password</Label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={form.password} onChange={(e) => upd("password", e.target.value)} type="password" autoComplete="new-password" required minLength={6} className="pl-9 h-11 rounded-xl" data-testid="register-password" />
          </div>
          <div className="text-[11px] text-[#5C5C5C] mt-1">Min. 6 karakter</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold">Konfirmasi Password</Label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={form.confirm_password} onChange={(e) => upd("confirm_password", e.target.value)} type="password" autoComplete="new-password" required minLength={6} className="pl-9 h-11 rounded-xl" data-testid="register-confirm-password" />
          </div>
          {form.confirm_password && form.password !== form.confirm_password && (
            <div className="text-[11px] brand-text-primary mt-1">Password tidak sama</div>
          )}
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 brand-btn-primary rounded-xl font-bold mt-2" data-testid="register-submit">
          {loading ? "Memproses..." : "Daftar Sekarang"}
        </Button>
        <div className="text-center text-sm text-[#5C5C5C]">
          Sudah punya akun? <Link to="/login" className="brand-text-primary font-bold" data-testid="goto-login">Masuk</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { default: api } = await import("@/lib/api");
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.dev_token) setToken(data.dev_token);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <AuthShell title="Lupa Password" subtitle="Masukkan email akun Anda">
      {sent ? (
        <div className="text-center space-y-3">
          <div className="text-sm text-[#5C5C5C]">Jika email terdaftar, link reset password telah dikirim.</div>
          {token && (
            <div className="brand-soft-bg rounded-xl p-3 text-xs">
              <div className="font-bold mb-1">[Dev Mode] Token reset:</div>
              <code className="break-all">{token}</code>
              <div className="mt-2"><Link to={`/reset-password?token=${token}`} className="brand-text-primary font-bold">Klik untuk reset</Link></div>
            </div>
          )}
          <Link to="/login" className="text-sm brand-text-primary font-bold">Kembali ke Login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email" className="h-11 rounded-xl" data-testid="forgot-email" />
          <Button type="submit" className="w-full h-12 brand-btn-primary rounded-xl font-bold" data-testid="forgot-submit">Kirim Link Reset</Button>
          <Link to="/login" className="block text-center text-sm brand-text-primary font-bold">Kembali ke Login</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <AuthShell title="Reset Password" subtitle="Buat password baru Anda">
      {done ? (
        <div className="text-center text-sm text-[#2D6A4F]">Password berhasil direset. Mengarahkan ke login...</div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input value={pw} onChange={(e) => setPw(e.target.value)} type="password" minLength={6} required placeholder="Password baru" className="h-11 rounded-xl" />
          <Button type="submit" className="w-full h-12 brand-btn-primary rounded-xl font-bold">Reset Password</Button>
        </form>
      )}
    </AuthShell>
  );
}

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen bg-[#FFFDF5] grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <LogoStacked size="lg" />
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-3xl p-6 sm:p-8 shadow-sm">
          <h1 className="font-display text-2xl font-bold text-[#4A2E1F]">{title}</h1>
          <p className="text-sm text-[#5C5C5C] mt-1 mb-5">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
